import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';
import { createSelector } from 'reselect';

import { HintItem } from '../../components/HintList/HintList';
import { getPhrasesSelectorInitialState } from '../../reducers/phrases-selector';
import { getPhrasesSelectorPersistentInitialData } from '../../reducers/phrases-selector/persistentData';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { CampaignType } from '../../typings/domain';
import { GroupId } from '../../typings/groups';
import { PhrasesSelectorPersistentData, PhrasesSelectorState, PhrasesSelectorWizardId } from '../../typings/phrases';
import { Regions, State } from '../../typings/state';

import * as keyset from './phrasesSelector.i18n';

const i18n = i18nFactory(keyset);

export const selectPhrasesSelectorWizard = (state: State, wizardId: PhrasesSelectorWizardId): PhrasesSelectorState =>
    state.phrasesSelectors[wizardId] || getPhrasesSelectorInitialState();

export const selectPhrasesSelectorPersistentData = (state: State, groupId: GroupId): PhrasesSelectorPersistentData =>
    state.phrasesSelectors.persistentData[groupId] || getPhrasesSelectorPersistentInitialData();

export const selectPhrasesSelectorData = createSelector(
    selectPhrasesSelectorWizard,
    (state: State, wizardId: PhrasesSelectorWizardId) => {
        const {
            groupInfo: { id }
        } = selectPhrasesSelectorWizard(state, wizardId);
        return selectPhrasesSelectorPersistentData(state, id);
    },
    (
        phrasesSelector: PhrasesSelectorState,
        persistentData: PhrasesSelectorPersistentData
    ): [PhrasesSelectorState, PhrasesSelectorPersistentData] => [phrasesSelector, persistentData]
);

export const selectPhrasesSelectorMinusPhrasesEnabled = createSelector(
    selectPhrasesSelectorWizard,
    (phrasesSelector: PhrasesSelectorState): boolean => {
        const {
            groupInfo: { campaign }
        } = phrasesSelector;

        return Boolean(campaign.type !== CampaignType.CPM_BANNER);
    }
);

export const selectPhrasesSelectorPriceRateEnabled = createSelector(
    selectPhrasesSelectorWizard,
    (state: State, wizardId: PhrasesSelectorWizardId) => wizardId,
    (phrasesSelector: PhrasesSelectorState, wizardId: PhrasesSelectorWizardId): boolean => {
        const {
            groupInfo: { campaign }
        } = phrasesSelector;

        return Boolean(
            campaign.type === CampaignType.CPM_BANNER &&
                // Кампаниям с автостратегиями ставка не нужна
                !campaign.strategy.isAutoBudget &&
                // На редактировании групп есть своя "ставка"
                wizardId !== PhrasesSelectorWizardId.editGroupsPage
        );
    }
);

export const selectPhrasesSelectorHintItems = createSelector(
    selectPhrasesSelectorWizard,
    selectPhrasesSelectorMinusPhrasesEnabled,
    (state: State) => state.commonData.regions,
    (
        phrasesSelector: PhrasesSelectorState,
        areMinusPhrasesEnabled: boolean,
        regions: Regions | undefined
    ): HintItem[] => {
        const { groupInfo } = phrasesSelector;

        return groupInfo
            ? compact([
                  {
                      label: i18n('campaign'),
                      text: groupInfo.campaign.name
                  },
                  {
                      label: i18n('group'),
                      text: groupInfo.name
                  },
                  {
                      label: i18n('region'),
                      text: regions ? RegionsHelpers.getRegionsTexts(regions, groupInfo.regionIds) : ''
                  },
                  areMinusPhrasesEnabled && {
                      label: i18n('minus-phrases-per-group'),
                      text: groupInfo.minusKeywords.join(', ') || i18n('not-specified')
                  }
              ])
            : [];
    }
);
